import { Cell, flexRender, Row } from '@tanstack/react-table';
import styles from '../../bmweDataTable.module.css';
import { CSSProperties, KeyboardEvent, MouseEvent } from 'react';
import { BmweColumnMeta } from '../../../../types';
import { toReadableDateString, toEuro } from '../../../../helper';
import { BooleanDisplay } from '../../../booleanDisplay';
import { useTruncationTitle } from '../../hooks/useTruncationTitle';

export interface DataRowProps<TRow extends Record<string, unknown>> {
  row: Row<TRow>;
  virtualStart: number;
  virtualIndex: number;
  ariaRowIndex: number;
  measure: (el: Element | null) => void;
  getRowAriaLabel?: (row: TRow) => string;
  isSelected?: boolean;
  /** Makes the row selectable; clicks on controls inside a cell are ignored. */
  onSelect?: (rowId: string) => void;
}

const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, label, [role="button"], [role="checkbox"]';

export function DataRow<TRow extends Record<string, unknown>>({
  row,
  virtualStart,
  virtualIndex,
  ariaRowIndex,
  measure,
  getRowAriaLabel,
  isSelected = false,
  onSelect,
}: DataRowProps<TRow>) {
  const handleCellMouseEnter = useTruncationTitle<HTMLTableCellElement>();
  const renderContent = (cell: Cell<TRow, unknown>, dataType: string) => {
    switch (dataType) {
      case 'date':
        return toReadableDateString(cell.getValue() as Date);
      case 'boolean':
        return <BooleanDisplay value={cell.getValue() as boolean} />;
      case 'currency':
        return toEuro(cell.getValue()) as string;
      default:
        return flexRender(cell.column.columnDef.cell, cell.getContext());
    }
  };

  const handleClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (!(event.target as Element).closest(INTERACTIVE_SELECTOR)) {
      onSelect?.(row.id);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    const target = event.target as Element;
    if (event.key === ' ' && target.getAttribute('role') === 'gridcell') {
      event.preventDefault();
      onSelect?.(row.id);
    }
  };

  const rowClassName = onSelect
    ? `${styles.bmweDataTableRow} ${styles.selectableRow} ${isSelected ? styles.selectedRow : ''}`
    : styles.bmweDataTableRow;

  return (
    <tr
      data-index={virtualIndex}
      ref={(node) => measure(node)}
      role="row"
      aria-rowindex={ariaRowIndex}
      aria-selected={onSelect ? isSelected : undefined}
      className={rowClassName}
      style={{ '--rowTranslateY': `${virtualStart}px` } as CSSProperties}
      aria-busy={false}
      onClick={onSelect && handleClick}
      onKeyDown={onSelect && handleKeyDown}
    >
      {row.getVisibleCells().map((cell, cellIndex) => {
        const meta = cell.column.columnDef.meta as BmweColumnMeta | undefined;
        const dataType = meta?.dataType ?? 'none';
        const justify = meta?.justify ?? 'start';
        return (
          <td
            key={cell.id}
            role="gridcell"
            tabIndex={cellIndex === 0 ? 0 : -1}
            aria-colindex={cell.column.getIndex() + 1}
            className={`${styles.bmweDataTableCell} ${styles.tableCell} ${styles.tableCellFocus} ${meta?.wrap ? styles.wrapCell : ''}`}
            style={
              {
                '--columnJustify': justify,
              } as CSSProperties
            }
            aria-label={
              getRowAriaLabel && cellIndex === 0
                ? getRowAriaLabel(row.original)
                : undefined
            }
            onMouseEnter={handleCellMouseEnter}
          >
            {renderContent(cell, dataType)}
          </td>
        );
      })}
    </tr>
  );
}
